import React from 'react';
import { 
  Info, 
  Cpu, 
  TrendingUp, 
  Target, 
  Globe, 
  ShieldCheck, 
  Code, 
  AlertTriangle 
} from 'lucide-react';

const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Инвестиции в искусственный интеллект: стратегия и перспективы
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Cpu className="w-5 h-5 text-green-500" />
          Что такое инвестиции в ИИ?
        </h3>
        <p>
          <strong>Инвестиции в искусственный интеллект</strong> - это вложение средств в компании, технологии и проекты, связанные с развитием и применением ИИ. Это включает инвестиции в разработку программного обеспечения, аппаратных решений, облачных технологий и робототехники.
        </p>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-indigo-500" />
          Ключевые направления инвестиций в ИИ
        </h3>
        <ul>
          <li><strong>Машинное обучение:</strong> Технологии, позволяющие системам учиться на основе данных.</li>
          <li><strong>Нейронные сети:</strong> Алгоритмы, имитирующие работу человеческого мозга.</li>
          <li><strong>Обработка естественного языка:</strong> Технологии понимания и генерации человеческой речи.</li>
          <li><strong>Компьютерное зрение:</strong> Системы распознавания и анализа изображений.</li>
          <li><strong>Робототехника:</strong> Интеграция ИИ в физические системы и механизмы.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Target className="w-5 h-5 text-orange-500" />
          Стратегии инвестирования в ИИ
        </h3>
        <ul>
          <li><strong>Диверсифицированная стратегия:</strong> Распределение инвестиций между различными секторами и технологиями ИИ.</li>
          <li><strong>Фокусная стратегия:</strong> Концентрация на наиболее перспективных направлениях и компаниях.</li>
          <li><strong>ETF-инвестиции:</strong> Вложения в биржевые фонды, специализирующиеся на технологиях ИИ.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-purple-500" />
          Географический охват инвестиций
        </h3>
        <p>
          Инвестиции в ИИ имеют глобальный характер. Ключевыми регионами являются:
        </p>
        <ul>
          <li>США (Кремниевая долина, центры разработок Google, Microsoft, OpenAI)</li>
          <li>Китай (Baidu, Alibaba, Tencent)</li>
          <li>Европа (Исследовательские центры и стартапы)</li>
          <li>Россия (Developing AI-ecosystems)</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-green-500" />
          Управление рисками
        </h3>
        <ul>
          <li>Диверсификация портфеля между различными секторами и технологиями</li>
          <li>Регулярный мониторинг технологических трендов</li>
          <li>Баланс между консервативными и агрессивными стратегиями</li>
          <li>Долгосрочный инвестиционный горизонт (5-10 лет)</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Code className="w-5 h-5 text-blue-500" />
          Технологический потенциал ИИ
        </h3>
        <p>
          Искусственный интеллект продолжает revolutionize различные отрасли:
        </p>
        <ul>
          <li>Здравоохранение (диагностика, персонализированная медицина)</li>
          <li>Финансы (риск-менеджмент, трейдинг, обнаружение мошенничества)</li>
          <li>Транспорт (беспилотные автомобили, логистика)</li>
          <li>Производство (оптимизация процессов, предиктивное обслуживание)</li>
          <li>Образование (персонализированное обучение)</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Важные предостережения
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Технологический прогресс непредсказуем</li>
            <li>Высокая волатильность инвестиций в новые технологии</li>
            <li>Необходимость постоянного обучения и адаптации</li>
            <li>Этические и регуляторные риски развития ИИ</li>
          </ul>
        </div>

        <p className="font-semibold mt-6">
          Инвестиции в ИИ - это не только финансовая стратегия, но и вложение в будущее технологического прогресса!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;